import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PaginatedContent, Response } from '@app/_models';
import { environment } from '@environments/environment';
import { concat, Observable, of, TimeoutError } from 'rxjs';
import { catchError, map, switchMap, tap, timeout } from 'rxjs/operators';

import { HandleError, HttpErrorHandlerService } from './http-error-handler.service';
import { logInDevelopment, LoggerService } from './logger.service';


@Injectable({
  providedIn: 'root'
})
export class BaseService {

  private handleError: HandleError;

  // Default timeouts (in milliseconds)
  private readonly DEFAULT_TIMEOUT = 30000; // 30 seconds for standard calls
  private readonly PAGINATED_TIMEOUT = 60000; // 60 seconds for paginated calls

  constructor(
    private http: HttpClient,
    httpErrorHandler: HttpErrorHandlerService,
    private logger: LoggerService
  ) {
    this.handleError = httpErrorHandler.createHandleError('BaseService');
  }

  public getOnceNEW<T>(endpoint: string, queryParams?: any, timeoutMs: number = this.DEFAULT_TIMEOUT): Observable<T> {
    const params = queryParams ? new HttpParams({ fromObject: queryParams }) : undefined;

    return this.http.get<Response<T>>(environment.apiURL + environment.apiVersion + endpoint, { params })
      .pipe(
        timeout(timeoutMs),
        logInDevelopment(`GET ${endpoint}`),
        map(response => {
          if (!response.success) {
            throw new Error(response.error);
          }
          return response.content;
        }),
        catchError(err => {
          if (err instanceof TimeoutError) {
            this.logger.warn(`Request timeout after ${timeoutMs}ms: GET ${endpoint}`);
          }
          return this.handleError(endpoint)(err);
        }),
      );
  }

  public getOnce<T>(endpoint: string, callback: (data: T) => void, timeoutMs: number = this.DEFAULT_TIMEOUT) {
    return this.http.get<Response<T>>(environment.apiURL + environment.apiVersion + endpoint)
      .pipe(
        timeout(timeoutMs),
        logInDevelopment(`GET ${endpoint} (callback)`),
        map(response => {
          if (response.success) {
            callback(response.content);
          }
          else {
            throw new Error(response.error);
          }
          return response;
        }),
        catchError(err => {
          if (err instanceof TimeoutError) {
            this.logger.warn(`Request timeout after ${timeoutMs}ms: GET ${endpoint} (callback)`);
          }
          return this.handleError(endpoint)(err);
        })

      );
  }

  public postRequestNEW<T>(data: any, endpoint: string, timeoutMs: number = this.DEFAULT_TIMEOUT): Observable<T> {
    return this.http.post<Response<T>>(environment.apiURL + environment.apiVersion + endpoint, data)
      .pipe(
        timeout(timeoutMs),
        logInDevelopment(`POST ${endpoint}`),
        map(response => {
          if (!response.success) {
            throw new Error(response.error);
          }
          return response.content;
        }),
        catchError(err => {
          if (err instanceof TimeoutError) {
            this.logger.warn(`Request timeout after ${timeoutMs}ms: POST ${endpoint}`);
          }
          return this.handleError(endpoint)(err);
        }),
      )
  }

  public postRequestNEWRestApiService<T>(data: any, endpoint: string, timeoutMs: number = this.DEFAULT_TIMEOUT): Observable<T> {
    return this.http.post<Response<T>>(environment.apiURLrestApiService + environment.apiVersion + endpoint, data)
      .pipe(
        timeout(timeoutMs),
        logInDevelopment(`POST RestAPI ${endpoint}`),
        map(response => {
          if (!response.success) {
            throw new Error(response.error);
          }
          return response.content;
        }),
        catchError(err => {
          if (err instanceof TimeoutError) {
            this.logger.warn(`Request timeout after ${timeoutMs}ms: POST RestAPI ${endpoint}`);
          }
          return this.handleError(endpoint)(err);
        }),
      )
  }

  public postRequest<T>(data: any, endpoint: string, callback: (data: T) => void, timeoutMs: number = this.DEFAULT_TIMEOUT) {
    return this.http.post<Response<T>>(environment.apiURL + environment.apiVersion + endpoint, data)
      .pipe(
        timeout(timeoutMs),
        logInDevelopment(`POST ${endpoint} (callback)`),
        map(response => {
          if (response.success) {
            callback(response.content);
          }
          else {
            throw new Error(response.error);
          }
          return response;
        }),
        catchError(err => {
          if (err instanceof TimeoutError) {
            this.logger.warn(`Request timeout after ${timeoutMs}ms: POST ${endpoint} (callback)`);
          }
          return this.handleError(endpoint)(err);
        })
      )
  }

  public getPaginatedNEW<T>(
    endpoint: string,
    page: number,
    amount: number,
    filters: any,
    timeoutMs: number = this.PAGINATED_TIMEOUT
  ): Observable<PaginatedContent<T>> {
    const params = {
      page: page,
      perPage: amount,
      ...filters
    };
    const url = `${environment.apiURL}${environment.apiVersion}${endpoint}`;
    return this.http.get<Response<PaginatedContent<T>>>(url, {
      params: new HttpParams({ fromObject: params })
    }).pipe(
      timeout(timeoutMs),
      map(response => response.content),
      catchError(err => {
        if (err instanceof TimeoutError) {
          this.logger.warn(`Request timeout after ${timeoutMs}ms: GET Paginated ${endpoint} (page ${page})`);
        }
        return this.handleError(endpoint)(err);
      })
    )
  }

  public getPaginated<T>(
    endpoint: string,
    page: number,
    amount: number,
    endpointRule: (
      endpoint: string,
      page: number,
      amount: number) => string,
    callback: (data: T) => void,
    timeoutMs: number = this.PAGINATED_TIMEOUT): any {
    return this._recursiveGetPage(endpoint, page, amount, endpointRule, callback, timeoutMs);
  }

  private _getPageBackground<T>(endpoint: string, timeoutMs: number) {
    return this.http.get<Response<T>>(environment.apiURL + environment.apiVersion + endpoint)
      .pipe(
        timeout(timeoutMs),
        catchError(err => {
          if (err instanceof TimeoutError) {
            this.logger.warn(`Request timeout after ${timeoutMs}ms: GET Page ${endpoint}`);
          }
          throw err;
        })
      );
  }

  private _recursiveGetPage<T>(
    endpoint: string,
    page: number,
    amount: number,
    endpointRule: (
      endpoint: string,
      page: number,
      amount: number) => string,
    callback: (data: T) => void,
    timeoutMs: number): any {
    return this._getPageBackground<T>(endpointRule(endpoint, page, amount), timeoutMs)
      .pipe(
        switchMap(response => {
          if (response.success && Array.isArray(response.content) && response.content.length > 0) {
            callback(response.content); //add new contents to an array
            return concat(
              of(response.content),
              this._recursiveGetPage(endpoint, page + 1, amount, endpointRule, callback, timeoutMs)
            )
          }
          else if (response.error) {
            throw new Error(response.error);
          }
          else {
            // Pagination complete
            return of(response.content);
          }
        }),
        catchError(this.handleError(endpoint))
      );
  }

}
